import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, FileText, Users } from 'lucide-react';

interface AccountInviteStatus {
  invited: boolean;
  requiresConfirmation?: boolean;
  message?: string;
  error?: string;
}

interface AccountCreationPromptProps {
  accountInviteStatus?: AccountInviteStatus | null;
  onCreateAccount: () => void;
  email?: string;
  isCreatingAccount: boolean;
  accountCreated: boolean;
  accountCreationError: string;
}

export const AccountCreationPrompt: React.FC<AccountCreationPromptProps> = ({
  onCreateAccount,
  email,
  isCreatingAccount,
  accountCreated,
  accountCreationError,
}) => {
  const navigate = useNavigate();

  return (
    <div className="ca-card p-4">
      <div className="flex items-center gap-3 mb-3">
        <FileText className="w-5 h-5 text-slate-600" />
        <h3 className="text-base font-semibold text-slate-900">Create account</h3>
      </div>
      {accountCreated ? (
        <div className="text-center py-4">
          <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-900 mb-1">
            {accountCreationError === 'existing-user'
              ? 'Account Already Exists!'
              : 'Account Created!'}
          </div>
          <div className="text-xs text-slate-600 mb-4">
            {accountCreationError === 'existing-user'
              ? `You already have an account with ${email || 'this email'}`
              : `Verification email sent to ${email || 'your email'}`}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-left">
            <p className="text-xs text-slate-900 mb-2">
              <strong>Next steps:</strong>
            </p>
            {accountCreationError === 'existing-user' ? (
              <ol className="text-xs text-blue-800 space-y-1 pl-4 list-decimal">
                <li>Click the button below to sign in</li>
                <li>
                  Use your existing password (or check email for temp password from checkout)
                </li>
                <li>This batch job will automatically appear in your dashboard</li>
              </ol>
            ) : (
              <ol className="text-xs text-blue-800 space-y-1 pl-4 list-decimal">
                <li>Check your email for the temporary password</li>
                <li>Click the button below to sign in</li>
                <li>This batch job will automatically appear in your dashboard</li>
              </ol>
            )}
          </div>
          <button
            onClick={() =>
              navigate(
                '/batch/auth?returnUrl=' + encodeURIComponent(window.location.pathname)
              )
            }
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-md text-sm font-medium transition-colors"
          >
            <Users className="w-4 h-4" />
            Sign In to View Dashboard
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-600 mb-3">
            Save processing history, share with teammates, and unlock volume discounts.
          </p>
          <div className="space-y-1 text-xs text-slate-600 mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Processing history dashboard</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Extended retention options</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span>Volume pricing</span>
            </div>
          </div>
          {accountCreationError && (
            <div className="mb-3 text-xs text-red-600 flex items-center gap-2 bg-red-50 border border-red-200 rounded-md p-2">
              <AlertTriangle className="w-3 h-3" />
              {accountCreationError}
            </div>
          )}
          <button
            onClick={onCreateAccount}
            disabled={isCreatingAccount}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-md text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isCreatingAccount ? (
              <>
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </>
      )}
    </div>
  );
};
